"use client";

import { useEffect, useState } from "react";
import { Box, Heading, Spinner, useTheme } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { Tooltip, Flex } from "@chakra-ui/react";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const Chart8 = ({ supplier, retailer }) => {
  const theme = useTheme();
  const [chartDiv, setChartDiv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    let root;

    const buildChart = async () => {
      if (!chartDiv) return;
      setLoading(true);

      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/rep_sales/chart8`, { supplier, retailer, userid: localStorage.getItem("userid") });

        if (!data?.data?.length) {
          setNoData(true);
          setLoading(false);
          return;
        }
        setNoData(false);

        if (root) root.dispose();
        root = am5.Root.new(chartDiv);
        root.setThemes([am5themes_Animated.new(root)]);

        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            paddingLeft: 10,
            paddingRight: 10,
            paddingBottom: 40,
          })
        );

        /* X-axis buckets */
        const xAxis = chart.xAxes.push(
          am5xy.CategoryAxis.new(root, {
            categoryField: "bucket",
            renderer: am5xy.AxisRendererX.new(root, {
              minGridDistance: 30,
              gridTemplate: { visible: false },
            }),
          })
        );

        xAxis.data.setAll(data.data);
        xAxis.get("renderer").labels.template.setAll({
          fontSize: 11,
          overshoot: 10,
          fill: am5.color("#9ca3af"),
        });
        xAxis.get("renderer").grid.template.setAll({ visible: false });

        /* Y-axis store counts */
        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            min: 0,
            strictMinMax: true,
            renderer: am5xy.AxisRendererY.new(root, { strokeOpacity: 0 }),
          })
        );
        yAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color("#9ca3af") });
        yAxis.get("renderer").grid.template.setAll({
          strokeOpacity: 0.15,
          strokeDasharray: [3, 3],
          stroke: am5.color("#e5e7eb"),
        });

        /* tooltip */
        const tooltip = am5.Tooltip.new(root, {
          getFillFromSprite: false,
          labelText: "{categoryX}: {valueY} stores",
        });
        tooltip.get("background").setAll({
          fill: am5.color("#ffffff"),
          fillOpacity: 0.95,
          stroke: am5.color("#e5e7eb"),
          strokeWidth: 1,
          shadowColor: am5.color("#000000"),
          shadowBlur: 8,
          shadowOffsetX: 0,
          shadowOffsetY: 2,
          shadowOpacity: 0.08,
          cornerRadius: 6,
        });
        tooltip.label.setAll({
          fill: am5.color("#374151"),
          fontSize: 12,
        });

        /* column series */
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "Stores",
            xAxis,
            yAxis,
            valueYField: "stores",
            categoryXField: "bucket",
            sequencedInterpolation: true,
          })
        );
        series.columns.template.setAll({
          tooltip: tooltip,
          tooltipText: "{categoryX}: {valueY} stores",
          strokeOpacity: 0,
          fillOpacity: 0.8,
          cornerRadiusTL: 4,
          cornerRadiusTR: 4,
          fill: am5.color("#58c087"),
        });
        series.data.setAll(data.data);
        series.appear();

        chart.appear(800, 400);
        setLoading(false);
      } catch (err) {
        console.error("Chart8 fetch failed", err);
        setLoading(false);
      }
    };

    buildChart();
    return () => {
      if (root) root.dispose();
    };
  }, [chartDiv, supplier, retailer]);

  return (
    <Box w="100%" h="100%" position="relative">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">Days-Cover Distribution</Heading>
        <Tooltip label="How many stores sit in each stock-cover band. Left bars (0-7 d) = nearly out; right bars (36 + d) = over-stocked." hasArrow placement="left" maxW="240px" whiteSpace="normal">
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      {noData && (
        <Flex align="center" justify="center" position="absolute" top="0" left="0" width="100%" height="100%" zIndex="1">
          <Heading size="sm" color="gray.600">
            No data available
          </Heading>
        </Flex>
      )}
      <div ref={setChartDiv} style={{ width: "100%", height: "calc(100% - 32px)" }} />
      {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
    </Box>
  );
};

export default Chart8;
